import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  UrlTree} from "@angular/router";
import { Store } from "@ngxs/store";
import { Observable, of } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";
import { Account, AccountType } from "../account/model/account.model";
import { GetAccount } from "../account/state/account.action";
import { AccountState } from "../account/state/account.state";

@Injectable({
  providedIn: "root"})
export class OwnCreatorProfileGuard implements CanActivate {
  constructor(
    private readonly store: Store,
    private readonly router: Router,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const routeName = (route.paramMap.get("name") ?? "").trim();

    if (!routeName) {
      return of(this.router.createUrlTree(["/"]));
    }

    return this.resolveAccount().pipe(
      map((account) => this.validateOwnership(account, routeName)),
      catchError(() => of(this.router.createUrlTree([`/${routeName}`]))),
    );
  }

  private resolveAccount(): Observable<Account | undefined> {
    return this.store.select(AccountState.account).pipe(
      take(1),
      switchMap((account) => {
        if (account) {
          return of(account);
        }

        return this.store.dispatch(new GetAccount()).pipe(
          switchMap(() => this.store.select(AccountState.account).pipe(take(1))),
          catchError(() => of(undefined)),
        );
      }),
    );
  }

  private validateOwnership(
    account: Account | undefined,
    routeName: string,
  ): boolean | UrlTree {
    const targetAtName = routeName.toLowerCase();
    const ownerAtName = account?.streamer?.atname?.toLowerCase();
    const isStreamer = account?.accountType === AccountType.Streamer;

    if (isStreamer && ownerAtName && ownerAtName === targetAtName) {
      return true;
    }

    return this.router.createUrlTree([`/${routeName}`]);
  }
}
